import { useState } from 'react';

const inputCss: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--line)',
  borderRadius: 10,
  fontSize: '0.875rem',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const smallBtn: React.CSSProperties = {
  background: '#fff',
  color: 'var(--green)',
  border: '1px solid var(--line)',
  borderRadius: 999,
  padding: '0.3rem 0.6rem',
  fontSize: '0.75rem',
  cursor: 'pointer',
  marginTop: 6,
};

const primaryBtn: React.CSSProperties = {
  background: 'var(--green)',
  color: '#fff',
  border: 'none',
  borderRadius: 999,
  padding: '0.45rem 0.85rem',
  fontSize: '0.8rem',
  cursor: 'pointer',
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

/**
 * Select with optional inline "create new option" panel.
 * Used for categories, locations, and similar catalogs.
 */
export function SelectWithCreate({
  label,
  value,
  onChange,
  options,
  emptyLabel = 'Nenhuma',
  createLabel = '+ Novo cadastro',
  createPlaceholder = 'Nome...',
  createHint,
  onCreate,
  creating,
}: {
  label: string;
  value: string;
  onChange: (id: string) => void;
  options: { id: string; name: string }[];
  emptyLabel?: string;
  createLabel?: string;
  createPlaceholder?: string;
  createHint?: string;
  onCreate: (name: string) => Promise<string | undefined> | string | undefined;
  creating?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  async function handleCreate() {
    setError('');
    if (!name.trim()) {
      setError('Informe um nome.');
      return;
    }
    try {
      const id = await onCreate(name.trim());
      if (id) onChange(id);
      setName('');
      setOpen(false);
    } catch {
      setError('Não foi possível cadastrar. Verifique a permissão ou tente outro nome.');
    }
  }

  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        fontSize: '0.8rem',
        color: 'var(--ink)',
        fontWeight: 600,
      }}
    >
      <span>{label}</span>
      <select
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        style={inputCss}
      >
        <option value="">{emptyLabel}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          setError('');
        }}
        style={smallBtn}
      >
        {createLabel}
      </button>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
          {createHint ? (
            <span style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>{createHint}</span>
          ) : null}
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              placeholder={createPlaceholder}
              style={{ ...inputCss, flex: 1 }}
            />
            <button
              type="button"
              onClick={() => {
                void handleCreate();
              }}
              disabled={creating}
              style={primaryBtn}
            >
              {creating ? '...' : 'Salvar'}
            </button>
          </div>
          {error ? (
            <span style={{ color: 'var(--danger)', fontSize: '0.75rem' }}>{error}</span>
          ) : null}
        </div>
      )}
    </label>
  );
}

export function PersonSelect({
  label = 'Pessoa',
  value,
  onChange,
  people,
  emptyLabel = 'Selecione...',
}: {
  label?: string;
  value: string;
  onChange: (id: string) => void;
  people: { id: string; name: string }[];
  emptyLabel?: string;
}) {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        fontSize: '0.8rem',
        color: 'var(--ink)',
        fontWeight: 600,
        flex: 1,
      }}
    >
      <span>{label}</span>
      <select
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        style={inputCss}
      >
        <option value="">{emptyLabel}</option>
        {people.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
    </label>
  );
}
